/**
 * HTTP client του API.
 *
 * Το frontend και το API μοιράζονται origin (nginx: SPA στο /, API στο /api),
 * οπότε το httpOnly refresh cookie ταξιδεύει χωρίς ρυθμίσεις CORS.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/**
 * Το access token ζει στο auth store. Ο client το ζητά μέσω callback αντί να
 * εισάγει το store, ώστε να μην δημιουργείται κυκλική εξάρτηση.
 */
let accessTokenGetter: () => string | null = () => null

export function setAccessTokenGetter(getter: () => string | null) {
  accessTokenGetter = getter
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>

  constructor(status: number, body: ApiErrorBody | null) {
    super(body?.error.message ?? 'API error')
    this.name = 'ApiError'
    this.status = status
    this.code = body?.error.code ?? 'network'
    this.details = body?.error.details ?? {}
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Ακατέργαστη διαδρομή χωρίς το πρόθεμα /api/v1 */
  absolute?: boolean
  /** Παραλείπει την κεφαλίδα Authorization, για δημόσια endpoints. */
  anonymous?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, absolute, anonymous, headers, ...rest } = options
  const token = anonymous ? null : accessTokenGetter()

  const response = await fetch(`${absolute ? '' : BASE_URL}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    let payload: ApiErrorBody | null = null
    try {
      payload = (await response.json()) as ApiErrorBody
    } catch {
      payload = null
    }
    throw new ApiError(response.status, payload)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/**
 * Κατεβάζει αρχείο από προστατευμένο endpoint.
 *
 * Ένα απλό `<a href>` δεν θα δούλευε: η ταυτοποίηση γίνεται με κεφαλίδα
 * `Authorization`, την οποία ο browser δεν στέλνει σε πλοήγηση. Το αρχείο
 * κατεβαίνει εδώ και ανοίγει ως blob.
 */
export async function download(path: string, filename: string): Promise<void> {
  const token = accessTokenGetter()
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  if (!response.ok) {
    let payload: ApiErrorBody | null = null
    try {
      payload = (await response.json()) as ApiErrorBody
    } catch {
      payload = null
    }
    throw new ApiError(response.status, payload)
  }

  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Η ανάκληση αργεί λίγο ώστε να προλάβει να ξεκινήσει η λήψη στο Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
